import type { TFunction } from 'i18next'

import { ApiError, type ErrorSubject } from './api'

/**
 * Turns whatever a failed request threw into a sentence in the user's language.
 *
 * The API's own message is English by design — it belongs to logs and to developers.
 * What travels for the user is a stable `code`, which maps to a translated key here.
 * An unrecognised code (a rule added on the server before the portal caught up) falls
 * back to the caller's generic message rather than leaking English into a Spanish
 * screen: less specific, still honest, never wrong.
 *
 * A sentence about another record names it (see `apiErrorSubject`); every other
 * sentence gets "another record" for its `{{name}}`, which no plain sentence uses.
 */
export function apiErrorMessage(caught: unknown, t: TFunction, fallbackKey: string): string {
  if (caught instanceof ApiError && caught.code !== null) {
    const key = `errors.${caught.code}`
    const translated = t(key, {
      name: apiErrorSubject(caught)?.name ?? t('registry.anotherRecord'),
    })

    // i18next echoes the key back when it has no translation for it.
    if (translated !== key) {
      return translated
    }
  }

  return t(fallbackKey)
}

/**
 * The record a failed request was about, when the API named one — the holder of a
 * taken number, on a refused registration or reactivation (TRKC-83). Null otherwise.
 */
export function apiErrorSubject(caught: unknown): ErrorSubject | null {
  if (!(caught instanceof ApiError) || caught.code === null) {
    return null
  }

  return caught.subjects[caught.code] ?? null
}
